import { clsx } from 'clsx';
import { Link } from 'react-admin';
import { makeStyles } from '@mui/styles';
import { useController } from 'react-hook-form';
import IconButton from '@mui/material/IconButton';
import { LoadingOutlined } from '@ant-design/icons';
import { Col, Input, notification, Row, Select } from 'antd';
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { cxHttp } from '@network';
import { exportToCsv } from '@utils';
import { InitialCard } from '@components';
import { CreditCall, CreditCallAnswer, CxCredit } from '@types';

interface Props {
  cxCredit: CxCredit;
  saveForm: () => Promise<void>;
}

type Call = Partial<CreditCall> & {key: string};

export const FollowUp = ({ cxCredit, saveForm }: Props) => {
  const creditId = cxCredit.id;
  const defaultCall = useRef<Call>({
    id: 0,
    key: '0',
    type: 'follow_up',
    observation: '',
    answers: [],
  } as Call);

  const {
    field: { onChange: changeFormCalls },
  } = useController({ name: 'calls' });

  const swiperRef = useRef<SwiperClass>(null);
  const [swiperSlide, setSwiperSlide] = useState<number>(0);
  const [calls, setCalls] = useState<Call[]>([]);
  const [updated, setUpdated] = useState<number>(0);
  const cls = useStyles();

  const loadCalls = useCallback(async () => {
    const {
      calls: loadedCalls,
      questions: loadedQuestions,
    } = await cxHttp.getFollowUpCalls(creditId);

    defaultCall.current.answers = loadedQuestions.map(question => ({
      id: 0,
      questionId: question.id,
      questionName: question.name,
      answer: '',
      questionPosition: question.position,
      questionEnabled: question.isEnabled,
      options: question.options,
    }));

    const nextCalls: Call[] = loadedCalls.map(call => ({
      ...call,
      key: call.id.toString(),
    }));

    if (nextCalls.length === 0) {
      nextCalls.push(defaultCall.current);
    }
    setCalls(nextCalls);
  }, [creditId]);

  // load calls
  useEffect(() => void loadCalls(), [creditId]);

  useEffect(() => {
    changeFormCalls(calls);
  }, [calls]);

  const updateCall = (updatedCall: Call) => {
    const nextCalls: Call[] = calls.map((call) => {
      if (updatedCall.key === call.key) {
        return updatedCall;
      }
      return call;
    });
    setCalls(nextCalls);
    setUpdated(prev => prev + 1);
  };

  const addCall = () => {
    if (calls.length >= 15) {
      return notification.error({ message: 'Limit is 15' });
    }
    const newCall = {
      ...defaultCall.current,
      id: 0,
      key: Date.now().toString(),
    } as Call;
    const nextCalls: Call[] = [...calls, newCall];
    setCalls(nextCalls);
    setTimeout(() => void swiperRef.current?.slideTo(nextCalls.length), 100);
    setUpdated(prev => prev + 1);
  };

  const removeCall = () => {
    if (calls.length === 1) {
      const newCall = {
        ...defaultCall.current,
        id: 0,
        key: Date.now().toString(),
      } as Call;
      setCalls([newCall]);
      setTimeout(() => void swiperRef.current?.slideTo(1), 100);
      setUpdated(prev => prev + 1);
    } else {
      const currentSlideIdx = swiperRef.current?.realIndex ?? -1;
      const nextCalls = calls.filter((_, idx) => idx !== currentSlideIdx);
      setCalls(nextCalls);
      setUpdated(prev => prev + 1);
    }
  };

  const downloadCalls = () => {
    exportToCsv(calls, `CX ${cxCredit.code} Follow Up`, (item: CreditCall) => {
      const itemExport = {
        'Call': item.title,
        'Credit Code': cxCredit.code,
        'Accredited': cxCredit.accreditedName ?? '',
        'Credit Status': cxCredit.statusEn ?? '',
      };
      for (const answer of item.answers || []) {
        itemExport[answer.questionName || ''] = answer.answer || '';
      }
      return itemExport;
    });
  };

  const gotoNextSlide = () => swiperRef.current.slideNext();
  const gotoPrevSlide = () => swiperRef.current.slidePrev();

  const isSaving = false;

  const save = async () => {
    changeFormCalls(calls);
    await saveForm();
    setTimeout(async () => await loadCalls(), 1000);
  };

  return (
    <InitialCard autoHeight contentClass={cls.wrap}>
      <Swiper
        maxBackfaceHiddenSlides
        onSwiper={(swiper: any) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper: any) => {
          setSwiperSlide(swiper.activeIndex);
        }}
        centeredSlides={true}
        spaceBetween={0}
        slidesPerView={1}
        keyboard={{ enabled: true }}
        navigation={true}
        modules={[Keyboard, Navigation, Pagination]}
        className={cls.swiper}
        pagination={{ type: 'fraction', clickable: true }}
        noSwiping
        touchStartPreventDefault={false}
        simulateTouch={false}
      >
        {calls.map((call, idx) => (
          <SwiperSlide key={call.key} virtualIndex={idx}>
            <Row gutter={[60, 60]}>
              <Col xs={24} sm={12}>
                <div className={cls.header}>
                  <div className={cls.headerTitle}>Follow Up #{idx + 1}</div>
                </div>
                {call && <CallAnswersForm call={call} updateCall={updateCall} />}
              </Col>

              <Col xs={24} sm={12}>
                <div className={cls.header}>
                  <div className={cls.headerTitle}>Call Log</div>
                  <div className={cls.headerControls}>
                    {call.id ? (
                      <Link to={`/credit-call/${call.id}`}>
                        <IconButton
                          className={clsx(cls.btn, cls.btnText)}
                          onClick={downloadCalls}>
                          <span>Go to Task</span>
                        </IconButton>
                      </Link>
                    ) : null}
                    <IconButton className={cls.btn} onClick={downloadCalls}>
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      className={clsx(cls.btn, updated && cls.btnSaveUpdated)}
                      onClick={save}
                    >
                      {isSaving ? <LoadingOutlined /> : <SaveIcon />}
                    </IconButton>
                    <IconButton className={clsx(cls.btn, cls.btnRemove)} onClick={removeCall}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton className={cls.btn} onClick={gotoPrevSlide}>
                      <LeftIcon />
                    </IconButton>
                    <div className={cls.headerText}>Follow Up #{swiperSlide + 1}</div>
                    <IconButton className={cls.btn} onClick={gotoNextSlide}>
                      <RightIcon />
                    </IconButton>
                    <IconButton
                      className={cls.btnAdd}
                      onClick={addCall}
                    >+</IconButton>
                  </div>
                </div>
                <textarea
                  name="comment"
                  onChange={e => {
                    if (call) updateCall({ ...call, observation: e.target.value });
                  }}
                  className={cls.textarea}
                  placeholder="Extra comments about the credit progress"
                  value={call?.observation || ''}
                />
              </Col>
            </Row>
          </SwiperSlide>
        ))}
      </Swiper>
    </InitialCard>
  );
};

const CallAnswersForm = (props: {call: Call, updateCall: (call: Call) => void}) => {
  const { call, updateCall } = props;
  const answers = (call.answers || []) as CreditCallAnswer[];
  const cls = useStyles();

  return (
    <div>
      {answers
        .filter(a => a.isFixed ? true : a.questionEnabled)
        .sort((a, b) => a.questionPosition - b.questionPosition)
        .map((answer, index) => (
          <div key={answer.questionId} className={cls.question}>
            <div className={cls.questionName}>
              {index + 1}. {answer.questionName}
            </div>
            {answer.isFixed ? (
              <div>{transformAnswer(answer.answer)}</div>
            ) : (
              <>
                {answer.options ? (
                  <Select
                    bordered={false}
                    className={cls.questionSelect}
                    options={Array.isArray(answer.options) ? answer.options : []}
                    onSelect={(val) => {
                      const updatedAnswers = answers.map(a => {
                        return a.questionId === answer.questionId ? { ...a, answer: val } : a;
                      });
                      updateCall({ ...call, answers: updatedAnswers });
                    }}
                    value={transformAnswer(answer.answer)}
                  />
                ) : (
                  <Input
                    value={transformAnswer(answer.answer)}
                    className={clsx(cls.textField)}
                    bordered={false}
                    onChange={e => {
                      const updatedAnswers = answers.map(a => {
                        return a.questionId === answer.questionId
                          ? { ...a, answer: e.target.value } : a;
                      });
                      updateCall({ ...call, answers: updatedAnswers });
                    }}
                  />
                )}
              </>
            )}
          </div>
        ))}
    </div>
  );
};

const transformAnswer = (str: string | null): string =>
  str ? (str.charAt(0).toUpperCase() + str.slice(1)).replace('Yes', 'Sí') : '';

const useStyles = makeStyles({
  wrap: {
    display: 'grid',
    width: '100%',
  },
  container: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    marginBottom: 12,
  },
  headerTitle: {
    color: 'var(--title-color)',
    fontSize: 20,
    fontWeight: 600,
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 2,
  },
  headerText: {
    paddingRight: 10,
    color: 'var(--text-color)',
    fontSize: 15,
    textAlign: 'center',
  },
  swiper: {
    width: '100%',
    maxWidth: '100%',
    position: 'initial',
    '& .swiper-button-prev': {
      display: 'none',
    },
    '& .swiper-button-next': {
      display: 'none',
    },
    '& .swiper-pagination': {
      position: 'relative!important',
    },
    '& .swiper-pagination-lock': {
      display: 'block !important',
    },
    '& .swiper-pagination-fraction': {
      bottom: '20px !important',
    },
    transform: 'translate3d(0,0,0)',
    '-webkit-backface-visibility': 'hidden',
    backfaceVisibility: 'hidden',
  },
  btn: {
    marginRight: 10,
    height: 20,
    width: 29,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--primary-color) !important',
    color: 'white',
    fontSize: 12,
    borderRadius: 4,
    opacity: 0.45,
    '&:hover': {
      opacity: 1,
      boxShadow: '0 4px 8px 0 rgba(48, 55, 61, 0.2)',
    },
  },
  btnText: {
    width: 'auto !important',
  },
  btnSaveUpdated: {
    opacity: 0.8,
  },
  btnRemove: {
    backgroundColor: '#FF0000 !important',
  },
  btnAdd: {
    marginRight: 0,
    width: 29,
    height: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--primary-color) !important',
    borderRadius: 4,
    opacity: 0.45,
    color: 'white',
    '&:hover': {
      opacity: 1,
    },
    '&:disabled': {
      color: 'white',
      cursor: 'not-allowed',
    },
  },
  question: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
  },
  questionName: {
    fontWeight: 600,
    fontSize: 14,
    width: '50%',
  },
  questionSelect: {
    marginTop: -10,
    width: 80,
    borderBottom: '1px solid var(--primary-color)',
    '& svg': {
      color: 'var(--primary-color)',
    },
  },
  textarea: {
    width: 'calc(100% - 2px)',
    minHeight: 160,
    display: 'block',
    padding: '14px 24px',
    fontSize: 14,
    fontFamily: 'var(--font-primary)',
    color: 'var(--text-color)',
    border: '1px solid var(--logo-color)',
    borderRadius: 8,
    outline: 'none',
    backgroundColor: 'var(--background-color)',
  },
  textField: {
    borderBottom: '1px solid var(--logo-color) !important',
    borderRadius: '0!important',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1,
    color: 'var(--text-color)',
    backgroundColor: 'var(--background-color)',
  },
});

const SaveIcon = () => (
  <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_3965_24270)">
      <path d="M14.8964 2.25077L13.2321 0.60257C13.1643 0.538906 13.075 0.5 12.9786 0.5H2.25C2.21429 0.5 2.18571 0.5 2.15 0.5C2.15 0.5 2.14643 0.5 2.14286 0.5H0.357143C0.160714 0.5 0 0.659161 0 0.85369V15.1463C0 15.3408 0.160714 15.5 0.357143 15.5H14.6429C14.8393 15.5 15 15.3408 15 15.1463V2.50189C15 2.40639 14.9607 2.31797 14.8964 2.25077ZM7.5 12.3486C6.11786 12.3486 5 11.2416 5 9.87279C5 8.50401 6.11786 7.39696 7.5 7.39696C8.88214 7.39696 10 8.50401 10 9.87279C10 11.2416 8.88214 12.3486 7.5 12.3486ZM11.4286 4.40474C11.4286 4.99187 10.95 5.46581 10.3571 5.46581H4.64286C4.05 5.46581 3.57143 4.99187 3.57143 4.40474V1.90769C3.57143 1.72377 3.72143 1.57522 3.90714 1.57522L7.91071 1.56814C8.07857 1.56814 8.21429 1.70255 8.21429 1.86878V3.97324C8.21429 4.20667 8.40357 4.39413 8.63929 4.39413H9.575C9.81071 4.39413 10 4.20667 10 3.97324V1.86171C10 1.69547 10.1357 1.56107 10.3036 1.56107H11.0929C11.2786 1.56107 11.4286 1.70962 11.4286 1.89354V4.40474Z"
        fill="#ffffff" />
    </g>
    <defs>
      <clipPath id="clip0_3965_24270">
        <rect width="15" height="15" fill="white" transform="translate(0 0.5)" />
      </clipPath>
    </defs>
  </svg>
);

const DeleteIcon = () => (
  <svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.32511 2.40625C8.35111 2.51427 8.46113 2.60028 8.57314 2.60028H10.7034C11.0895 2.60028 11.4035 2.91432 11.4035 3.30037C11.4035 3.62042 11.1895 3.88845 10.8975 3.97246L10.5314 14.0258C10.5014 14.8439 9.82131 15.5 9.0012 15.5H2.40232C1.58421 15.5 0.90212 14.8419 0.872116 14.0258L0.506067 3.97246C0.214029 3.88845 0 3.61842 0 3.30037C0 2.91432 0.314042 2.60028 0.700093 2.60028H2.83038C2.94039 2.60028 3.05041 2.51227 3.07841 2.40625L3.22843 1.80617C3.41445 1.06608 4.13855 0.5 4.90065 0.5H6.50287C7.26497 0.5 7.99107 1.06608 8.17509 1.80617L8.32511 2.40625ZM5.20069 5.80071V12.6016C5.20069 12.8777 5.42472 13.1017 5.70076 13.1017C5.9768 13.1017 6.20083 12.8777 6.20083 12.6016V5.80071C6.20083 5.52467 5.9768 5.30064 5.70076 5.30064C5.42472 5.30064 5.20069 5.52467 5.20069 5.80071ZM2.90039 5.81471L3.10041 12.6156C3.10841 12.8917 3.33844 13.1097 3.61448 13.1017C3.89052 13.0937 4.10855 12.8636 4.10055 12.5876L3.90052 5.78671C3.89252 5.51067 3.66249 5.29264 3.38645 5.30064C3.11041 5.30864 2.89239 5.53867 2.90039 5.81471ZM7.501 5.7847L7.30097 12.5856C7.29297 12.8616 7.511 13.0917 7.78704 13.0997C8.06308 13.1077 8.29311 12.8897 8.30111 12.6136L8.50113 5.81271C8.50913 5.53667 8.29111 5.30664 8.01507 5.29864C7.73903 5.29064 7.509 5.50867 7.501 5.7847ZM4.57061 2.59828H6.82891C6.88292 2.59828 6.91692 2.55427 6.90492 2.50227L6.81491 2.14422C6.7849 2.0262 6.62088 1.89819 6.50087 1.89819H4.89865C4.77864 1.89819 4.61461 2.0262 4.58461 2.14422L4.4946 2.50227C4.4806 2.55627 4.5146 2.59828 4.57061 2.59828Z"
      fill="white" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="17" height="13" viewBox="0 0 17 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.2834 0.727662C7.2834 0.325785 7.62244 0 8.04067 0C8.45891 0 8.79795 0.325785 8.79795 0.727662V8.73195C8.79795 9.13382 8.45891 9.45961 8.04067 9.45961C7.62244 9.45961 7.2834 9.13382 7.2834 8.73195V0.727662Z"
      fill="white" />
    <path d="M4.2218 6.69966C3.92607 6.41549 3.92607 5.95476 4.2218 5.67059C4.51754 5.38642 4.99702 5.38642 5.29275 5.67059L8.50561 8.7578C8.80135 9.04197 8.80135 9.5027 8.50561 9.78687C8.20988 10.071 7.7304 10.071 7.43466 9.78687L4.2218 6.69966Z"
      fill="white" />
    <path d="M11.7782 6.69966C12.0739 6.41549 12.0739 5.95476 11.7782 5.67059C11.4825 5.38642 11.003 5.38642 10.7072 5.67059L7.49439 8.7578C7.19865 9.04197 7.19865 9.5027 7.49439 9.78687C7.79012 10.071 8.2696 10.071 8.56534 9.78687L11.7782 6.69966Z"
      fill="white" />
    <rect y="11" width="17" height="2" rx="1" fill="white" />
    <rect x="2" y="6" width="7" height="2" rx="1" transform="rotate(90 2 6)" fill="white" />
    <rect x="17" y="6" width="7" height="2" rx="1" transform="rotate(90 17 6)" fill="white" />
  </svg>
);

const LeftIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="matrix(-0.707107 -0.707107 -0.707107 0.707107 9.0166 2.60968)"
      fill="white" />
    <rect width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 6.8291 13.5853)"
      fill="white" />
  </svg>
);

const RightIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.763672"
      y="2.60962"
      width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="rotate(-45 0.763672 2.60962)"
      fill="white" />
    <rect x="2.95117"
      y="13.5852"
      width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="rotate(-135 2.95117 13.5852)"
      fill="white" />
  </svg>
);

